import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useChecklist } from '../contexts/ChecklistContext';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Button,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Security as SecurityIcon,
  PlayArrow as PlayArrowIcon,
  Quiz as QuizIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import Sidebar, { drawerWidth, collapsedDrawerWidth } from '../components/Sidebar';
import QuizResults from './QuizResults';

const DigitaleSelbstverteidigung = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { quizResult } = useChecklist();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showPrivacyDetails, setShowPrivacyDetails] = useState(false);

  const handleStartQuiz = () => {
    navigate('/quiz');
  };

  if (!user) {
    return null;
  }

  const hasCompletedQuiz = !!quizResult;

  // If quiz is completed, show QuizResults directly
  if (hasCompletedQuiz) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0f0f0f 100%)',
        }}
      >
        {/* Sidebar */}
        <Sidebar 
          open={sidebarOpen} 
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          mobileOpen={mobileSidebarOpen}
          onMobileToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        />
        
        <Box sx={{ 
          display: 'flex',
          ml: { sm: sidebarOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px` },
          transition: (theme) => theme.transitions.create('margin-left', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}>
          <Box sx={{ flexGrow: 1, width: '100%' }}>
            <QuizResults />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0f0f0f 100%)',
        position: 'relative',
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
        '&::before': {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(33, 150, 243, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(66, 165, 245, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
          zIndex: 0,
        }
      }}
    >
      {/* Sidebar */}
      <Sidebar 
        open={sidebarOpen} 
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        mobileOpen={mobileSidebarOpen}
        onMobileToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}
      />
      
      <Box sx={{ 
        display: 'flex',
        ml: { sm: sidebarOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px` },
        transition: (theme) => theme.transitions.create('margin-left', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        pt: { xs: 2, sm: 3, md: 4 },
        pb: 4,
      }}>
        <Container maxWidth="lg" sx={{ flexGrow: 1, width: '100%', position: 'relative', zIndex: 1 }}>
          {/* Zurück zum Lagebild Link */}
          <Box sx={{ mb: 2, position: 'relative', zIndex: 1 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/dashboard')}
              sx={{
                color: '#42a5f5',
                '&:hover': {
                  backgroundColor: 'rgba(66, 165, 245, 0.1)',
                },
              }}
            >
              Zurück zum Lagebild
            </Button>
          </Box>
          
          {/* Header */}
          <Box sx={{ marginBottom: 6, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, marginBottom: 2 }}>
              <SecurityIcon sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, color: '#42a5f5' }} />
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                Digitale Selbstverteidigung
              </Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: '#b0b0b0',
                fontSize: { xs: '1rem', md: '1.25rem' },
                lineHeight: 1.8,
                maxWidth: '800px',
                mx: 'auto',
              }}
            >
              Lerne, wie du dich im digitalen Raum schützen kannst
            </Typography>
          </Box>

          {/* Quiz Information Card */}
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              mb: 4,
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <QuizIcon sx={{ fontSize: '2.5rem', color: '#42a5f5' }} />
                <Typography
                  variant="h5"
                  sx={{
                    color: '#ffffff',
                    fontWeight: 600,
                  }}
                >
                  Finde deinen persönlichen Lernstil
                </Typography>
              </Box>
              
              <Typography
                variant="body1"
                sx={{
                  color: '#ffffff',
                  lineHeight: 1.8,
                  mb: 3,
                  fontSize: { xs: '0.95rem', md: '1.1rem' },
                }}
              >
                Mit unserem kurzen Quiz findest du heraus, welcher Sicherheitstyp du bist und erhältst 
                personalisierte Empfehlungen für deinen Weg zu mehr Sicherheit im digitalen Alltag. Das Quiz 
                dauert nur wenige Minuten und hilft dir dabei, die für dich passenden Module 
                und Lernmethoden zu entdecken.
              </Typography>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <PersonIcon sx={{ fontSize: '2.5rem', color: '#42a5f5', mb: 1 }} />
                    <Typography variant="h6" sx={{ color: '#ffffff', mb: 1, fontWeight: 600 }}>
                      Personalisiert
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                      Basierend auf deinen Antworten erhältst du individuelle Empfehlungen
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <SchoolIcon sx={{ fontSize: '2.5rem', color: '#42a5f5', mb: 1 }} />
                    <Typography variant="h6" sx={{ color: '#ffffff', mb: 1, fontWeight: 600 }}>
                      Schnell & einfach
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                      Nur 12 Fragen – in weniger als 5 Minuten fertig
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <SecurityIcon sx={{ fontSize: '2.5rem', color: '#42a5f5', mb: 1 }} />
                    <Typography variant="h6" sx={{ color: '#ffffff', mb: 1, fontWeight: 600 }}>
                      Praktisch
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                      Erhalte konkrete Module und Anleitungen für deine digitale Sicherheit
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Was wir speichern */}
          <Card sx={{ 
            mb: 4, 
            background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(15, 15, 15, 0.9) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#ffffff' }}>
                Was wir speichern (kurz)
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#b0b0b0' }}>
                Wir speichern deine Quiz-Antworten, deinen Sicherheitstyp und deinen Checklist-Fortschritt in der Datenbank. 
                Diese Daten sind mit deinem Account verknüpft und bleiben auf allen Geräten verfügbar. 
                Nicht zur Leistungsbewertung.
              </Typography>
              <Accordion 
                expanded={showPrivacyDetails} 
                onChange={() => setShowPrivacyDetails(!showPrivacyDetails)}
                sx={{ 
                  boxShadow: 'none', 
                  bgcolor: 'transparent',
                  '&:before': { display: 'none' },
                  '& .MuiAccordionSummary-root': {
                    color: '#ffffff',
                  },
                  '& .MuiAccordionDetails-root': {
                    color: '#b0b0b0',
                  }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#ffffff' }} />}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#ffffff' }}>
                    Details anzeigen
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, color: '#ffffff' }}>
                      Was wird gespeichert?
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: '#b0b0b0' }}>
                      Deine Quiz-Antworten und das daraus resultierende Sicherheitsprofil 
                      sowie dein Fortschritt bei den Checklist-Modulen (welche Module du abgeschlossen hast, 
                      welche als "erledigt" markiert sind).
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, color: '#ffffff' }}>
                      Wer sieht das?
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: '#b0b0b0' }}>
                      Nur du siehst deine Daten. Die Daten werden sicher in unserer Datenbank gespeichert und sind 
                      mit deinem Account verknüpft. Sie sind auf allen Geräten verfügbar, auf denen du dich einloggst.
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, color: '#ffffff' }}>
                      Wie lange?
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: '#b0b0b0' }}>
                      Die Daten bleiben gespeichert, bis du sie löschst. Du kannst jederzeit über die "Quiz erneut machen" 
                      Funktion alle Quiz-Daten und deinen Fortschritt zurücksetzen.
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, color: '#ffffff' }}>
                      Wofür genutzt?
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: '#b0b0b0' }}>
                      Um dir personalisierte Lernempfehlungen zu geben, deinen Fortschritt zu speichern und sicherzustellen, 
                      dass dein Lernpfad und deine Empfehlungen beim nächsten Login gleich bleiben.
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, color: '#ffffff' }}>
                      Wofür nicht?
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                      Nicht für Marketing, nicht für Leistungsbewertung, nicht für externe Analyse, nicht für Werbung.
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>

          {/* Action Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleStartQuiz}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    backgroundColor: '#2196f3',
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#1976d2',
                    }
                  }}
                >
                  Quiz starten
                </Button>
              </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default DigitaleSelbstverteidigung;

